// Uygulama kabuğu: oturum, dil, rota, marka filtresi ve uyarı rozeti.
// Sayfalar kendi verilerini api.ts üzerinden çeker; kabuk yalnızca hangi markaların
// seçili olduğunu ve kimin baktığını bilir.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getBrands, getNewAlertCount, logout as apiLogout, me } from './api';
import { hasBonusUsageBrand, hasDarkBrand, hasPanelPasswordBrand, hasWeeklyReportBrand, hasWhatsappBrand, LS, NAV, tzOfBrands, type RouteKey } from './constants';
import { clearRoster, useDocumentClick, useEscape } from './hooks';
import { useTranslateDOM } from './i18n';
import { EmptyState, Icon, Skeleton } from './ui';
import type { AgentFocus, Brand, BrandId, Lang, PanelUser, Theme } from './types';

import AgentDetail from './pages/AgentDetail';
import ListDetail from './pages/ListDetail';
import BonusUsage from './pages/BonusUsage';
import Alerts from './pages/Alerts';
import Breaks from './pages/Breaks';
import Dark from './pages/Dark';
import LiveStatus from './pages/LiveStatus';
import Login from './pages/Login';
import PanelPasswords from './pages/PanelPasswords';
import ExtFleet from './pages/ExtFleet';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Sms from './pages/Sms';
import Whatsapp from './pages/Whatsapp';
import Winback from './pages/Winback';
import WeeklyReport from './pages/WeeklyReport';
import CustomerCalls from './pages/CustomerCalls';

// ── localStorage (oturum kalıcılığı) ─────────────────────────────────────────
const readJSON = <T,>(k: string, d: T): T => {
  try {
    const v = JSON.parse(localStorage.getItem(k) as string);
    return v == null ? d : (v as T);
  } catch {
    return d;
  }
};
const readStr = (k: string, d: string): string => {
  try {
    return localStorage.getItem(k) ?? d;
  } catch {
    return d;
  }
};
const save = (k: string, v: unknown) => {
  try {
    localStorage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v));
  } catch {
    /* kota dolu / özel mod — oturum yine de bellekte çalışır */
  }
};
const clearSaved = () => {
  try {
    [LS.session, LS.brands, LS.route].forEach((k) => localStorage.removeItem(k));
  } catch {
    /* yoksay */
  }
};

// Nav görünürlüğü: `adminOnly` sayfalar (Ayarlar, Eklenti) admin'e özel; süpervizör diğer
// sayfaları admin gibi görür (Haftalık Rapor dahil, tam); veri kısıtı yalnız marka kapsamı.
// `feature` sayfalar (Winback) KİŞİ bazlıdır: sunucunun /me'de verdiği bayrak true değilse
// rol ne olursa olsun görünmez (sahibin kuralı: yalnız asya ve neo).
const navAllowed = (n: (typeof NAV)[number], user: PanelUser | null) => {
  if (!user) return false;
  if (n.adminOnly && user.role !== 'admin') return false;
  if (n.feature && user.features?.[n.feature] !== true) return false;
  return true;
};

/** Görev #168 (2026-10-04): `/winback` adresi Winback'i AYRI ARAÇ olarak açar — aynı oturum,
 *  aynı sunucu kapısı (403 rotada), panel menüsü/uyarı rozeti yok. Sunucu her bilinmeyen yolu
 *  index.html'e düşürür (src/http/static.js SPA fallback), main.js mutlak yoldan yüklenir. */
const isWinbackPath = (): boolean => {
  try {
    return window.location.pathname.replace(/\/+$/, '') === '/winback';
  } catch {
    return false;
  }
};

export default function App() {
  const appRef = useRef<HTMLDivElement>(null);
  const [standalone] = useState(isWinbackPath);

  const [user, setUser] = useState<PanelUser | null>(() => readJSON<PanelUser | null>(LS.session, null));
  const [lang, setLangState] = useState<Lang>(() => (readStr(LS.lang, 'tr') === 'en' ? 'en' : 'tr'));
  const [theme, setThemeState] = useState<Theme>(() => (readStr(LS.theme, 'dark') === 'light' ? 'light' : 'dark'));
  const [route, setRoute] = useState<RouteKey>(() => readStr(LS.route, 'canli-durum') as RouteKey);
  const [brands, setBrands] = useState<BrandId[]>(() => readJSON<BrandId[]>(LS.brands, []));
  const [availableBrands, setAvailableBrands] = useState<Brand[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [alertNewCount, setAlertNewCount] = useState(0);
  const [agentFocus, setAgentFocus] = useState<AgentFocus | null>(null);

  // EN kaplaması — her commit'ten sonra (bkz. i18n.ts).
  useTranslateDOM(lang, appRef);

  // Açılışta sunucu oturumunu doğrula: localStorage anlık boyama içindir,
  // gerçek otorite httpOnly çerezdir. 401 → yerel oturumu düşür.
  useEffect(() => {
    let alive = true;
    me()
      .then((fresh) => {
        if (!alive) return;
        if (fresh) {
          save(LS.session, fresh);
          setUser(fresh);
        } else {
          clearSaved();
          setUser(null);
        }
      })
      .catch(() => {
        /* ağ hatası: eldeki oturumla devam — ilk 401'de zaten düşer */
      });
    return () => {
      alive = false;
    };
  }, []);

  // api.ts herhangi bir istekte 401 görürse bu olayı yayınlar.
  useEffect(() => {
    const onUnauthorized = () => {
      clearSaved();
      setUser(null);
      setBrands([]);
      setAvailableBrands([]);
      setRoute('canli-durum');
    };
    window.addEventListener('caa:unauthorized', onUnauthorized);
    return () => window.removeEventListener('caa:unauthorized', onUnauthorized);
  }, []);

  // Oturum açıkken erişilebilir markaları çöz; kaydedilmiş marka/rota seçimini doğrula.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    getBrands().then((all) => {
      if (!alive) return;
      const avail = all.filter((b) => user.brands.includes(b.id));
      setAvailableBrands(avail);
      setBrands((prev) => {
        const persisted = prev.filter((id) => avail.some((b) => b.id === id));
        return persisted.length ? persisted : avail.map((b) => b.id);
      });
      setRoute((r) => (NAV.some((n) => n.key === r && navAllowed(n, user)) ? r : 'canli-durum'));
    });
    return () => {
      alive = false;
    };
  }, [user]);

  // DARK içeriği yalnızca kapsamda DARK kullanan bir marka (Vegas/Prime) varsa görünür.
  // Boş seçim marka çözülene dek geçicidir → availableBrands'e düş (nav titremesin).
  const darkVisible = hasDarkBrand(brands.length ? brands : availableBrands.map((b) => b.id));

  // Yalnız BetBooker seçiliyken DARK sayfasında kalınamaz — Canlı Durum'a dön.
  useEffect(() => {
    if (user && availableBrands.length && route === 'dark' && !darkVisible) {
      setRoute('canli-durum');
      save(LS.route, 'canli-durum');
    }
  }, [route, darkVisible, availableBrands.length, user]);

  // Haftalık Rapor yalnız Vegas/Prime içindir (WEEKLY_REPORT_BRANDS) — DARK ile
  // AYNI desen, ayrı sabit. Boş seçim marka çözülene dek geçicidir → availableBrands'e düş.
  const weeklyVisible = hasWeeklyReportBrand(brands.length ? brands : availableBrands.map((b) => b.id));

  useEffect(() => {
    // Liste Detayı da haftalık rapordan okur (görev #222) → aynı kapı.
    if (user && availableBrands.length && (route === 'haftalik-rapor' || route === 'liste-detayi') && !weeklyVisible) {
      setRoute('canli-durum');
      save(LS.route, 'canli-durum');
    }
  }, [route, weeklyVisible, availableBrands.length, user]);

  // WhatsApp sayfası yalnız paylaşılan WhatsApp dosyasında sekmesi olan markalar içindir
  // (WHATSAPP_BRANDS: Vegas + Prime) — Haftalık Rapor ile AYNI desen, ayrı sabit.
  const whatsappVisible = hasWhatsappBrand(brands.length ? brands : availableBrands.map((b) => b.id));

  useEffect(() => {
    if (user && availableBrands.length && route === 'whatsapp' && !whatsappVisible) {
      setRoute('canli-durum');
      save(LS.route, 'canli-durum');
    }
  }, [route, whatsappVisible, availableBrands.length, user]);

  // Panel Şifreleri yalnız panel girişi olan markalar içindir (PANEL_PASSWORD_BRANDS: Vegas, Prime,
  // Hera) — süpervizör dahil, kendi markalarıyla sınırlı (görev #216/#217). Aynı desen.
  const panelPwVisible = hasPanelPasswordBrand(brands.length ? brands : availableBrands.map((b) => b.id));

  useEffect(() => {
    if (user && availableBrands.length && route === 'panel-sifreleri' && !panelPwVisible) {
      setRoute('canli-durum');
      save(LS.route, 'canli-durum');
    }
  }, [route, panelPwVisible, availableBrands.length, user]);

  // Bonus Kullanımı (sahip 2026-10-27): Vegas, Prime, Prime VIP, Hera — aynı desen.
  const bonusVisible = hasBonusUsageBrand(brands.length ? brands : availableBrands.map((b) => b.id));

  useEffect(() => {
    if (user && availableBrands.length && route === 'bonus-kullanimi' && !bonusVisible) {
      setRoute('canli-durum');
      save(LS.route, 'canli-durum');
    }
  }, [route, bonusVisible, availableBrands.length, user]);

  const refreshAlertBadge = useCallback(() => {
    // Markalar çözülene kadar sayma (giriş sonrası bir kare boş kalıyor — tasarımda
    // bu pencere yoktu). Çözüldükten sonra boş seçim "filtre yok" demektir ve api
    // tüm yeni uyarıları döner; Uyarılar sayfası da aynı şekilde davranır.
    // Süpervizörün yalnızca kendi markalarını görmesi Phase 1'de sunucu tarafında.
    // Süpervizör Uyarılar sayfasını görür → rozet onun için de sayılır.
    if (!user || !availableBrands.length || standalone) {
      setAlertNewCount(0);
      return;
    }
    getNewAlertCount(brands)
      .then((n) => setAlertNewCount(n))
      .catch(() => {
        /* rozet kritik değil; bir sonraki yenilemede düzelir */
      });
  }, [user, brands, availableBrands.length, standalone]);

  useEffect(() => {
    refreshAlertBadge();
  }, [refreshAlertBadge]);

  useEffect(() => {
    if (standalone) document.title = 'Winback';
  }, [standalone]);

  useDocumentClick(menuOpen, () => setMenuOpen(false));
  useEscape(menuOpen, () => setMenuOpen(false));

  const setLang = (l: Lang) => {
    save(LS.lang, l);
    setLangState(l);
  };

  // Tema — dil gibi bir tercih: her geçişte kaydedilir, çıkışta silinmez. index.html'deki satır
  // içi betik aynı anahtarı ilk boyamadan önce okur (açılışta koyu kare yanıp sönmez).
  const setTheme = (t: Theme) => {
    save(LS.theme, t);
    setThemeState(t);
  };
  useLayoutEffect(() => {
    if (theme === 'light') document.documentElement.setAttribute('data-theme', 'light');
    else document.documentElement.removeAttribute('data-theme');
  }, [theme]);

  const go = (key: RouteKey) => {
    setRoute(key);
    setMenuOpen(false);
    save(LS.route, key);
  };

  // Canlı Durum kartından temsilci detayına atlama. `n` bir nonce: aynı temsilciye
  // ikinci kez tıklanınca da prop değişsin (yoksa AgentDetail effect'i tetiklenmez).
  const goAgent = (agentId: string) => {
    setAgentFocus((f) => ({ id: agentId, n: (f ? f.n : 0) + 1 }));
    go('ajan-detayi');
  };

  const toggleBrand = (id: BrandId) => {
    setBrands((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      save(LS.brands, next);
      return next;
    });
  };

  const onLoginSuccess = (u: PanelUser) => {
    save(LS.session, u);
    save(LS.route, 'canli-durum');
    setUser(u);
    setRoute('canli-durum');
    setMenuOpen(false);
    // Markalar user effect'inde çözülür; eski seçimi hemen düşür ki
    // bir önceki kullanıcının markaları bir kare boyunca sızmasın.
    setBrands([]);
    setAvailableBrands([]);
  };

  const logout = () => {
    void apiLogout(); // sunucu çerezini düşür (hata yutulur)
    clearSaved();
    clearRoster();    // modül-düzeyi roster önbelleği bir sonraki kullanıcıya sızmasın
    setUser(null);
    setMenuOpen(false);
    setBrands([]);
    setAvailableBrands([]);
    setRoute('canli-durum');
  };

  const vars: React.CSSProperties = {
    // Renk jetonları (koyu + açık) index.html :root'ta — <html data-theme> seçer; body ve
    // kaydırma çubukları da aynı jetonlara bakar. QMS puan bantları --good/--warn/--bad'i okur.
    minHeight: '100vh',
    background: 'var(--bg)',
    color: 'var(--text)',
    fontFamily: "'Inter',system-ui,-apple-system,sans-serif",
    WebkitFontSmoothing: 'antialiased',
  };

  // key: dil değişince ağaç yeniden monte olur → DOM taze TR'ye döner, sonra
  // useTranslateDOM (EN ise) yeniden çevirir. State bu div'in ÜSTÜNDE durduğu
  // için remount oturumu/rotayı sıfırlamaz.
  return (
    <div key={'lang-' + lang} ref={appRef} style={vars}>
      {!user ? (
        // Demo hesap paneli Phase 3'te kapalı — gerçek kullanıcılar sunucuda.
        <Login onSuccess={onLoginSuccess} showDemo={false} />
      ) : standalone ? (
        <WinbackStandalone user={user} availableBrands={availableBrands} lang={lang} theme={theme} onSetLang={setLang} onSetTheme={setTheme} onLogout={logout} />
      ) : (
        <Shell
          user={user}
          route={route}
          brands={brands}
          availableBrands={availableBrands}
          darkVisible={darkVisible}
          weeklyVisible={weeklyVisible}
          whatsappVisible={whatsappVisible}
          panelPwVisible={panelPwVisible}
          bonusVisible={bonusVisible}
          lang={lang}
          theme={theme}
          menuOpen={menuOpen}
          alertNewCount={alertNewCount}
          agentFocus={agentFocus}
          onGo={go}
          onGoAgent={goAgent}
          onToggleBrand={toggleBrand}
          onSetLang={setLang}
          onSetTheme={setTheme}
          onToggleMenu={() => setMenuOpen((m) => !m)}
          onLogout={logout}
          onAlertsChanged={refreshAlertBadge}
        />
      )}
    </div>
  );
}

// ── Winback ayrı araç kabuğu (`/winback`, görev #168) ─────────────────────────
// Panelin kenar çubuğu yok: tek sayfa, ince üst bar (dil, panele dönüş, çıkış). Erişim yine
// KİŞİ bazlı — listede olmayan oturum sayfayı değil "erişim yok"u görür (sunucu da 403 verir).
function WinbackStandalone({ user, availableBrands, lang, theme, onSetLang, onSetTheme, onLogout }: {
  user: PanelUser;
  availableBrands: Brand[];
  lang: Lang;
  theme: Theme;
  onSetLang: (l: Lang) => void;
  onSetTheme: (t: Theme) => void;
  onLogout: () => void;
}) {
  const allowed = user.features?.winback === true;
  const langBtn = (on: boolean): React.CSSProperties => ({
    padding: '5px 11px', fontSize: '11.5px', fontWeight: 700, border: 'none', borderRadius: '7px', cursor: 'pointer',
    background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--text)' : 'var(--faint)',
  });
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 28px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
          <Icon name="person_search" size={21} color="#fff" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-.01em', lineHeight: 1.2 }}>Winback</div>
          <div style={{ fontSize: '12px', color: 'var(--dim)' }}>Müşteri ID ile yatırım, bonus ve oyun analizi</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: 'var(--accent)', textDecoration: 'none' }}>
            <Icon name="headset_mic" size={17} color="var(--accent)" />
            <span>Call Agent paneli</span>
          </a>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '2px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '9px' }}>
            <button onClick={() => onSetLang('tr')} style={langBtn(lang === 'tr')}>
              TR
            </button>
            <button onClick={() => onSetLang('en')} style={langBtn(lang === 'en')}>
              EN
            </button>
          </div>
          <ThemeSwitch theme={theme} onSetTheme={onSetTheme} track="var(--bg)" />
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{user.displayName}</span>
          <button
            onClick={onLogout}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 11px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--bad-text)', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer' }}
          >
            <Icon name="logout" size={17} />
            <span>Çıkış</span>
          </button>
        </div>
      </header>
      <main style={{ flex: 1, padding: '24px 28px' }}>
        {!allowed ? (
          <EmptyState icon="lock" title="Erişim yok" reason="Bu sayfa için yetkiniz yok." />
        ) : availableBrands.length === 0 ? (
          <Skeleton heights={[72, 160]} />
        ) : (
          <Winback brands={availableBrands.map((b) => b.id)} lang={lang} standalone />
        )}
      </main>
    </div>
  );
}

// ── Tema anahtarı (açık / koyu) — TR|EN seçicisinin ikizi ───────────────────
function ThemeSwitch({ theme, onSetTheme, track }: { theme: Theme; onSetTheme: (t: Theme) => void; track: string }) {
  const btn = (on: boolean): React.CSSProperties => ({
    width: '30px', height: '25px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: 'none', borderRadius: '7px', cursor: 'pointer',
    background: on ? 'var(--accent-soft)' : 'transparent', color: on ? 'var(--text)' : 'var(--faint)',
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '2px', background: track, border: '1px solid var(--border)', borderRadius: '9px' }}>
      <button onClick={() => onSetTheme('light')} title="Açık tema" aria-label="Açık tema" aria-pressed={theme === 'light'} style={btn(theme === 'light')}>
        <Icon name="light_mode" size={17} />
      </button>
      <button onClick={() => onSetTheme('dark')} title="Koyu tema" aria-label="Koyu tema" aria-pressed={theme === 'dark'} style={btn(theme === 'dark')}>
        <Icon name="dark_mode" size={17} />
      </button>
    </div>
  );
}

// ── Kabuk (kenar çubuğu + üst bar + içerik) ──────────────────────────────────

// Daraltılmış ray: ikonun merkezi iki durumda da x=36'da kalır (nav 12 + kenar 1 + dolgu 12 +
// ikon 22/2) — genişlik animasyonu yalnız etiketleri açar/kapar, hiçbir şey zıplamaz.
const NAV_W = 248;
const RAIL_W = 72;
const NAV_EASE = '.22s cubic-bezier(.4,0,.2,1)';

function Shell({
  user,
  route,
  brands,
  availableBrands,
  darkVisible,
  weeklyVisible,
  whatsappVisible,
  panelPwVisible,
  bonusVisible,
  lang,
  theme,
  menuOpen,
  alertNewCount,
  agentFocus,
  onGo,
  onGoAgent,
  onToggleBrand,
  onSetLang,
  onSetTheme,
  onToggleMenu,
  onLogout,
  onAlertsChanged,
}: {
  user: PanelUser;
  route: RouteKey;
  brands: BrandId[];
  availableBrands: Brand[];
  darkVisible: boolean;
  weeklyVisible: boolean;
  whatsappVisible: boolean;
  panelPwVisible: boolean;
  bonusVisible: boolean;
  lang: Lang;
  theme: Theme;
  menuOpen: boolean;
  alertNewCount: number;
  agentFocus: AgentFocus | null;
  onGo: (r: RouteKey) => void;
  onGoAgent: (agentId: string) => void;
  onToggleBrand: (id: BrandId) => void;
  onSetLang: (l: Lang) => void;
  onSetTheme: (t: Theme) => void;
  onToggleMenu: () => void;
  onLogout: () => void;
  onAlertsChanged: () => void;
}) {
  const isAdmin = user.role === 'admin';
  const activeKey: RouteKey = route;
  const nav = NAV.find((n) => n.key === activeKey) || NAV[0];
  const initials = user.displayName
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  // Başlıktaki "bugün" de KAPSAMIN dilimindedir: panelin günü marka günüdür
  // (format.ts todayInTz). Yalnızca BetBooker seçiliyken İstanbul 00:00–03:00 arası
  // tarayıcı bir sonraki günü gösterir, veriler ise hâlâ Akra'nın dünüdür.
  const dateLabel = new Date().toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', {
    timeZone: tzOfBrands(brands.length ? brands : availableBrands.map((b) => b.id)),
    day: 'numeric', month: 'short', year: 'numeric',
  });

  const selNames = availableBrands.filter((b) => brands.includes(b.id)).map((b) => b.name);
  const activeBrandsLabel =
    availableBrands.length === 0 ? '…' : selNames.length === 0 ? 'Yok' : selNames.length === availableBrands.length ? 'Tümü' : selNames.join(', ');
  // Tasarım burada supervisor için 'Süpervizör · Vegas + Prime' sabitliyordu (mock artığı);
  // gerçek marka listesinden türetiyoruz — demo kullanıcı için sonuç aynı.
  const userRoleFull = isAdmin ? 'Yönetici · Tüm markalar' : user.roleLabel + ' · ' + availableBrands.map((b) => b.name).join(' + ');

  const langBtn = (on: boolean): React.CSSProperties => ({
    padding: '5px 11px',
    fontSize: '11.5px',
    fontWeight: 700,
    border: 'none',
    borderRadius: '7px',
    cursor: 'pointer',
    background: on ? 'var(--accent-soft)' : 'transparent',
    color: on ? 'var(--text)' : 'var(--faint)',
  });

  // Kenar çubuğu daraltma — dil gibi bir tercih: localStorage'da durur, çıkışta silinmez.
  const [navCollapsed, setNavCollapsed] = useState(() => readStr(LS.nav, '') === '1');
  const [logoHover, setLogoHover] = useState(false);
  const [tip, setTip] = useState<{ label: string; badge: number; top: number; height: number } | null>(null);
  const toggleNav = () => {
    const next = !navCollapsed;
    save(LS.nav, next ? '1' : '0');
    setNavCollapsed(next);
    setLogoHover(false);
    setTip(null);
  };
  // Daraltılmış rayda etiket yerine sağa açılan ipucu. nav'ın overflow'u absolute bir balonu
  // keserdi → fixed + öğenin rect'i. Metin bir DOM düğümü (title değil) → i18n gözlemcisi çevirir.
  const showTip = (e: React.MouseEvent<HTMLElement>, label: string, badge = 0) => {
    if (!navCollapsed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, badge, top: r.top, height: r.height });
  };
  const hideTip = () => setTip(null);
  const fade: React.CSSProperties = { opacity: navCollapsed ? 0 : 1, transition: 'opacity .15s', whiteSpace: 'nowrap' };
  const logoAsToggle = navCollapsed && logoHover;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* ── KENAR ÇUBUĞU ── */}
      <aside
        style={{
          width: (navCollapsed ? RAIL_W : NAV_W) + 'px',
          flex: 'none',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          borderRight: '1px solid var(--border)',
          overflow: 'hidden',
          transition: 'width ' + NAV_EASE,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '19px 18px', borderBottom: '1px solid var(--border)' }}>
          {/* Daraltılmışken logo "genişlet" düğmesidir: hover'da ikon panel-aç'a döner. */}
          <button
            onClick={navCollapsed ? toggleNav : undefined}
            onMouseEnter={(e) => {
              if (!navCollapsed) return;
              setLogoHover(true);
              showTip(e, 'Menüyü genişlet');
            }}
            onMouseLeave={() => {
              setLogoHover(false);
              hideTip();
            }}
            tabIndex={navCollapsed ? 0 : -1}
            aria-label={navCollapsed ? 'Menüyü genişlet' : undefined}
            aria-expanded={navCollapsed ? false : undefined}
            data-caa-navbtn={navCollapsed ? '1' : undefined}
            style={{
              width: '36px',
              height: '36px',
              padding: 0,
              borderRadius: '10px',
              background: logoAsToggle ? 'var(--surface2)' : 'var(--accent)',
              border: logoAsToggle ? '1px solid var(--border2)' : '1px solid transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              cursor: navCollapsed ? 'pointer' : 'default',
              transition: 'background .15s, border-color .15s',
            }}
          >
            <Icon name={logoAsToggle ? 'left_panel_open' : 'headset_mic'} size={21} color={logoAsToggle ? 'var(--text)' : '#fff'} />
          </button>
          <div style={{ minWidth: 0, ...fade }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-.01em', lineHeight: 1.2 }}>Call Agent</div>
            <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '.18em', color: 'var(--accent)' }}>ADMIN</div>
          </div>
          <button
            onClick={toggleNav}
            title="Menüyü daralt"
            aria-expanded={!navCollapsed}
            tabIndex={navCollapsed ? -1 : 0}
            data-caa-navbtn="1"
            style={{
              marginLeft: 'auto',
              width: '30px',
              height: '30px',
              padding: 0,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: '1px solid transparent',
              borderRadius: '8px',
              color: 'var(--faint)',
              cursor: 'pointer',
              opacity: navCollapsed ? 0 : 1,
              pointerEvents: navCollapsed ? 'none' : 'auto',
              transition: 'opacity .15s, background .15s, border-color .15s, color .15s',
            }}
          >
            <Icon name="left_panel_close" size={20} />
          </button>
        </div>

        <nav
          onScroll={hideTip}
          data-caa-rail={navCollapsed ? '1' : undefined}
          style={{ flex: 1, padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: '4px', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: navCollapsed ? 'none' : undefined }}
        >
          {NAV.filter(
            (n) => navAllowed(n, user) && (n.key !== 'dark' || darkVisible) && (n.key !== 'haftalik-rapor' || weeklyVisible) && (n.key !== 'liste-detayi' || weeklyVisible) && (n.key !== 'bonus-kullanimi' || bonusVisible) && (n.key !== 'whatsapp' || whatsappVisible) && (n.key !== 'panel-sifreleri' || panelPwVisible),
          ).map((n) => {
            const active = n.key === activeKey;
            const badge = n.key === 'uyarilar' ? alertNewCount : 0;
            return (
              <div
                key={n.key}
                onClick={() => onGo(n.key)}
                onMouseEnter={(e) => showTip(e, n.label, badge)}
                onMouseLeave={hideTip}
                data-caa-navitem="1"
                data-active={active ? '1' : undefined}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  color: active ? 'var(--text)' : 'var(--dim)',
                  background: active ? 'var(--accent-soft)' : 'transparent',
                  border: active ? '1px solid var(--accent-border)' : '1px solid transparent',
                  fontWeight: active ? 600 : 500,
                  transition: 'background .15s, color .15s',
                }}
              >
                <Icon name={n.icon} size={22} color={active ? '#3d8bff' : 'var(--faint)'} />
                <span style={{ flex: 1, fontSize: '13.5px', ...fade }}>{n.label}</span>
                {/* Daraltılmış rayda rozet ikonun köşesine biner; tam sayı ipucunda. */}
                {navCollapsed && badge > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '3px',
                      right: '3px',
                      minWidth: '16px',
                      height: '16px',
                      padding: '0 4px',
                      borderRadius: '999px',
                      background: '#ef5d5d',
                      color: '#fff',
                      fontSize: '9.5px',
                      fontWeight: 700,
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 0 2px var(--surface)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
                {!navCollapsed && badge > 0 && (
                  <span
                    style={{
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 5px',
                      borderRadius: '999px',
                      background: '#ef5d5d',
                      color: '#fff',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {badge}
                  </span>
                )}
                {!navCollapsed && active && <span style={{ width: '6px', height: '6px', borderRadius: '999px', background: 'var(--accent)', flex: 'none' }} />}
              </div>
            );
          })}
        </nav>

        <div
          onMouseEnter={(e) => showTip(e, 'Sistem aktif')}
          onMouseLeave={hideTip}
          style={{
            padding: navCollapsed ? '14px 0 14px 32.5px' : '14px 16px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            transition: 'padding ' + NAV_EASE,
          }}
        >
          <span style={{ width: '7px', height: '7px', borderRadius: '999px', background: '#37c46b', animation: 'caaPulse 2s ease-in-out infinite', flex: 'none' }} />
          <span style={{ fontSize: '11.5px', color: 'var(--dim)', ...fade }}>Sistem aktif</span>
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--faint)', ...fade }}>v0.1</span>
        </div>
      </aside>

      {navCollapsed && tip && (
        <div
          style={{ position: 'fixed', left: RAIL_W + 8 + 'px', top: tip.top + 'px', height: tip.height + 'px', display: 'flex', alignItems: 'center', pointerEvents: 'none', zIndex: 60 }}
        >
          <div
            key={tip.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              background: 'var(--surface2)',
              border: '1px solid var(--border2)',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0,0,0,.45)',
              fontSize: '12.5px',
              fontWeight: 600,
              color: 'var(--text)',
              whiteSpace: 'nowrap',
              animation: 'caaTip .12s ease both',
            }}
          >
            <span>{tip.label}</span>
            {tip.badge > 0 && (
              <span style={{ minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '999px', background: '#ef5d5d', color: '#fff', fontSize: '10.5px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}>
                {tip.badge}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── ANA ALAN ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, containerType: 'inline-size' }}>
        {/* ÜST BAR */}
        <header
          style={{
            minHeight: '64px',
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '10px 24px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--header-bg)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Icon name="filter_alt" size={20} color="var(--faint)" />
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', flexShrink: 0 }}>
            {availableBrands.map((b) => {
              const on = brands.includes(b.id);
              return (
                <button
                  key={b.id}
                  onClick={() => onToggleBrand(b.id)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '7px 13px',
                    borderRadius: '999px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: on ? 'var(--text)' : 'var(--dim)',
                    background: on ? 'var(--accent-soft)' : 'transparent',
                    border: on ? '1px solid var(--accent-border)' : '1px solid var(--border)',
                    transition: 'background .15s, border-color .15s, color .15s',
                  }}
                >
                  <span style={{ width: '7px', height: '7px', borderRadius: '999px', background: on ? '#3d8bff' : 'var(--muted)', flex: 'none' }} />
                  <span>{b.name}</span>
                </button>
              );
            })}
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '2px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '9px' }}>
              <button onClick={() => onSetLang('tr')} style={langBtn(lang === 'tr')}>
                TR
              </button>
              <button onClick={() => onSetLang('en')} style={langBtn(lang === 'en')}>
                EN
              </button>
            </div>
            <ThemeSwitch theme={theme} onSetTheme={onSetTheme} track="var(--surface)" />
            <div data-caa-date="1" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--dim)', fontSize: '13px', whiteSpace: 'nowrap' }}>
              <Icon name="calendar_today" size={18} />
              <span>{dateLabel}</span>
            </div>
            <div style={{ width: '1px', height: '26px', background: 'var(--border)', flex: 'none' }} />

            <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={onToggleMenu}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '6px 8px 6px 6px',
                  background: 'transparent',
                  border: '1px solid transparent',
                  borderRadius: '10px',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '9px',
                    background: 'var(--accent-soft)',
                    border: '1px solid var(--accent-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 700,
                    color: 'var(--accent)',
                    flex: 'none',
                  }}
                >
                  {initials}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', lineHeight: 1.15 }}>{user.displayName}</span>
                  <span style={{ fontSize: '11px', color: 'var(--dim)', lineHeight: 1.15 }}>{user.roleLabel}</span>
                </span>
                <Icon name="expand_more" size={20} color="var(--faint)" />
              </button>

              {menuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 8px)',
                    width: '236px',
                    background: 'var(--surface)',
                    border: '1px solid var(--border2)',
                    borderRadius: '12px',
                    padding: '6px',
                    boxShadow: '0 14px 34px rgba(0,0,0,.5)',
                    animation: 'caaFade .14s ease both',
                    zIndex: 50,
                  }}
                >
                  <div style={{ padding: '11px 11px 12px', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid var(--border)', marginBottom: '6px' }}>
                    <span
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '9px',
                        background: 'var(--accent-soft)',
                        border: '1px solid var(--accent-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: 'var(--accent)',
                        flex: 'none',
                      }}
                    >
                      {initials}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{user.displayName}</div>
                      <div style={{ fontSize: '11px', color: 'var(--dim)' }}>{userRoleFull}</div>
                    </div>
                  </div>
                  <button
                    onClick={onLogout}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'var(--bad-text)',
                      fontSize: '13px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <Icon name="logout" size={19} />
                    <span>Çıkış</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* İÇERİK */}
        <main data-caa-main="1" style={{ flex: 1, overflowY: 'auto', padding: '32px 36px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '28px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}
            >
              <Icon name={nav.icon} size={24} color="var(--accent)" />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, letterSpacing: '-.02em', color: 'var(--text)' }}>{nav.label}</h1>
              <p style={{ margin: '4px 0 0', fontSize: '13.5px', color: 'var(--dim)', textWrap: 'pretty' } as React.CSSProperties}>{nav.sub}</p>
            </div>
            <div
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 14px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon name="tune" size={17} color="var(--faint)" />
              <span style={{ fontSize: '12.5px', color: 'var(--dim)' }}>Aktif marka:</span>
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>{activeBrandsLabel}</span>
            </div>
          </div>

          {/* Süpervizör admin gibi tüm sayfalarda gezer — Haftalık Rapor dahil, KENDİ markalarının
              tam raporu (görev #147/#148, 2026-09-30; eski scorecard-only budaması emekli). Kısıt
              yalnız marka kapsamı (sunucu scope.js); Ayarlar admin'e özel kalır. */}
          {route === 'canli-durum' && (
            <LiveStatus brands={brands} onGoAlerts={() => onGo('uyarilar')} onGoAgent={onGoAgent} isAdmin={isAdmin} lang={lang} />
          )}
          {route === 'raporlar' && <Reports brands={brands} />}
          {route === 'haftalik-rapor' && weeklyVisible && <WeeklyReport brands={brands} lang={lang} />}
          {route === 'ajan-detayi' && <AgentDetail brands={brands} focus={agentFocus} />}
          {route === 'liste-detayi' && weeklyVisible && <ListDetail brands={brands} />}
          {route === 'bonus-kullanimi' && bonusVisible && <BonusUsage brands={brands.length ? brands : availableBrands.map((b) => b.id)} />}
          {route === 'molalar' && <Breaks brands={brands} isAdmin={isAdmin} />}
          {route === 'uyarilar' && <Alerts brands={brands} availableBrands={availableBrands} onAlertsChanged={onAlertsChanged} />}
          {route === 'sms' && <Sms brands={brands} lang={lang} onGoAgent={onGoAgent} />}
          {route === 'whatsapp' && whatsappVisible && <Whatsapp brands={brands} lang={lang} />}
          {route === 'winback' && user.features?.winback === true && <Winback brands={brands} lang={lang} />}
          {route === 'butun-aramalar' && <CustomerCalls brands={brands.length ? brands : availableBrands.map((b) => b.id)} lang={lang} />}
          {route === 'dark' && darkVisible && <Dark brands={brands} onGoSettings={() => onGo('ayarlar')} />}
          {route === 'panel-sifreleri' && panelPwVisible && (
            <PanelPasswords brands={brands.length ? brands : availableBrands.map((b) => b.id)} isAdmin={isAdmin} />
          )}
          {route === 'eklenti' && isAdmin && <ExtFleet brands={brands} lang={lang} />}
          {route === 'ayarlar' && isAdmin && <Settings brands={brands} allBrands={availableBrands.map((b) => b.id)} />}
        </main>
      </div>
    </div>
  );
}
